import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  LifeBuoy, ArrowLeft, ArrowRight, Wine, Wind, UserRound, Users, Siren,
  ShieldCheck, HeartHandshake, CheckCircle2, Phone, Loader2, Heart,
} from "lucide-react";
import { toast } from "sonner";
import { professionalLabel } from "@/lib/professional";

const OPTIONS = [
  { key: "urge", label: "Estou com vontade de beber, usar ou jogar", icon: Wine },
  { key: "anxiety", label: "Estou muito ansioso", icon: Wind },
  { key: "counselor", label: "Preciso falar com meu conselheiro", icon: UserRound },
  { key: "network", label: "Preciso falar com alguém da minha rede de apoio", icon: Users },
];

const URGE_TIPS = [
  "Afaste-se agora da situação, local, app ou pessoa ligada ao impulso.",
  "Espere alguns minutos antes de qualquer decisão.",
  "Faça uma pausa e apenas observe o impulso — ele passa.",
  "Lembre dos seus motivos para seguir no tratamento.",
  "Coloque em prática uma estratégia combinada com seu conselheiro.",
  "Procure alguém da sua rede de apoio.",
];
const ANX_TIPS = [
  "Respire devagar: inspire, segure, solte por mais tempo.",
  "Grounding: 5 coisas que você vê, 4 que ouve, 3 que toca.",
  "Mude de ambiente por alguns minutos.",
  "Foque em algo concreto ao seu redor.",
  "Procure alguém de confiança.",
];

const TRIGGER = {
  urge: "substance_or_gambling_urge",
  anxiety: "anxiety",
  counselor: "counselor_request",
  network: "support_network_request",
};

export default function NeedSupport() {
  const nav = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [screen, setScreen] = useState("menu");
  const [origin, setOrigin] = useState("network");
  const [preselect, setPreselect] = useState(null);
  const [urgency, setUrgency] = useState(false);

  const loadCtx = () => api.get("/me/help-context").then((r) => setCtx(r.data));
  useEffect(() => { loadCtx(); }, []);

  const openUrgency = async () => {
    setUrgency(true);
    try { await api.post("/help-requests", { trigger_context: "emergency", recipient_type: null }); } catch {}
  };

  const goCoping = (k) => setScreen(k === "urge" ? "coping-urge" : "coping-anxiety");
  const goAsk = (originKey, pre) => { setOrigin(originKey); setPreselect(pre); setScreen("ask"); };

  return (
    <div className="max-w-xl mx-auto space-y-5 animate-fade-up pb-10">
      {screen !== "menu" ? (
        <button onClick={() => setScreen("menu")} className="text-sm text-[#0F766E] flex items-center gap-1" data-testid="help-back"><ArrowLeft size={15} /> Voltar</button>
      ) : (
        <button onClick={() => nav("/")} className="text-sm text-[#0F766E] flex items-center gap-1" data-testid="help-home"><ArrowLeft size={15} /> Início</button>
      )}

      {screen === "menu" && (
        <>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#EF4444] grid place-items-center text-white shrink-0"><LifeBuoy size={24} /></div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-[#1F3B4D]">Pedir ajuda</h1>
              <p className="text-sm text-[#94A3B8]">Você não precisa lidar com isso sozinho.</p>
            </div>
          </div>
          <div className="grid gap-3">
            {OPTIONS.map((o) => (
              <button key={o.key} data-testid={`help-option-${o.key}`}
                onClick={() => o.key === "urge" || o.key === "anxiety" ? goCoping(o.key) : goAsk(o.key, o.key === "counselor" ? "counselor" : "support_network")}
                className="w-full text-left flex items-center gap-4 p-5 rounded-2xl bg-white border-2 border-[#E0F2FE] hover:border-[#0F766E] transition-all card-hover">
                <div className="w-11 h-11 rounded-xl bg-[#0F766E]/10 grid place-items-center shrink-0"><o.icon size={22} className="text-[#0F766E]" /></div>
                <span className="font-semibold text-[#1F3B4D] text-base">{o.label}</span>
              </button>
            ))}
            <button data-testid="help-option-emergency" onClick={openUrgency}
              className="w-full text-left flex items-center gap-4 p-5 rounded-2xl bg-[#EF4444] text-white shadow-lg hover:bg-[#dc2626] transition-all">
              <div className="w-11 h-11 rounded-xl bg-white/20 grid place-items-center shrink-0"><Siren size={22} /></div>
              <span className="font-bold text-base">Preciso de atendimento de urgência</span>
            </button>
          </div>
        </>
      )}

      {(screen === "coping-urge" || screen === "coping-anxiety") && (
        <CopingScreen
          context={screen === "coping-urge" ? "urge" : "anxiety"}
          ctx={ctx}
          onAskHelp={() => goAsk(screen === "coping-urge" ? "urge" : "anxiety", null)}
        />
      )}

      {screen === "ask" && (
        <AskForHelp ctx={ctx} preselect={preselect} triggerContext={TRIGGER[origin]}
          onNeedNetwork={() => nav("/rede-apoio")} onBack={() => setScreen("menu")} />
      )}

      {/* Urgency dialog */}
      <Dialog open={urgency} onOpenChange={setUrgency}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading text-[#EF4444] flex items-center gap-2"><Siren size={20} /> Atendimento de urgência</DialogTitle></DialogHeader>
          <p className="text-sm text-[#1F3B4D]">Se você está em risco imediato, ligue agora. O Compasso Cuidar não substitui serviços de emergência.</p>
          <div className="grid gap-2 mt-2">
            <a href="tel:192" data-testid="call-samu" className="p-4 rounded-xl bg-[#EF4444] text-white font-bold text-center flex items-center justify-center gap-2"><Phone size={18} /> SAMU · 192</a>
            <a href="tel:188" data-testid="call-cvv" className="p-4 rounded-xl bg-[#0F766E] text-white font-bold text-center flex items-center justify-center gap-2"><Phone size={18} /> CVV · 188</a>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CopingScreen({ context, ctx, onAskHelp }) {
  const strategies = (ctx?.strategies || []).filter((s) => s.context === context || s.context === "general");
  const reasons = ctx?.personal_reasons || [];
  const tips = context === "urge" ? URGE_TIPS : ANX_TIPS;
  const title = context === "urge" ? "Vamos passar por esse momento" : "Vamos respirar juntos";

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-[#34D399]/20 grid place-items-center shrink-0"><Heart size={22} className="text-[#0F766E]" /></div>
        <h1 className="font-heading text-2xl font-bold text-[#1F3B4D]">{title}</h1>
      </div>

      {strategies.length > 0 && (
        <Card className="rounded-2xl p-5 border-0 text-white" style={{ background: "linear-gradient(120deg,#0F766E,#1F3B4D)" }} data-testid="coping-plan-strategies">
          <div className="flex items-center gap-2 text-[#34D399] text-xs font-semibold uppercase tracking-wider mb-3"><ShieldCheck size={15} /> Suas estratégias combinadas</div>
          <div className="space-y-2.5">
            {strategies.map((s, i) => (
              <div key={s.id || i} className="flex items-start gap-2 text-[15px]"><CheckCircle2 size={18} className="text-[#34D399] shrink-0 mt-0.5" /> {s.title}</div>
            ))}
          </div>
        </Card>
      )}

      {reasons.length > 0 && (
        <Card className="rounded-2xl p-5 border-[#E0F2FE] bg-[#E0F2FE]/40" data-testid="coping-reasons">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#0F766E] mb-2">Seus motivos</div>
          <div className="space-y-1.5">
            {reasons.map((r, i) => <div key={r.id || i} className="text-[15px] text-[#1F3B4D] flex gap-2"><Heart size={16} className="text-[#EF4444] shrink-0 mt-0.5" /> {r.title}</div>)}
          </div>
        </Card>
      )}

      <Card className="rounded-2xl p-5 border-[#E0F2FE]">
        <div className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8] mb-2">O que ajuda agora</div>
        <div className="space-y-2.5">
          {tips.map((t, i) => <div key={i} className="text-[15px] text-[#1F3B4D] flex gap-2"><span className="text-[#34D399] font-bold">•</span> {t}</div>)}
        </div>
      </Card>

      <Button onClick={onAskHelp} data-testid="coping-ask-help-button"
        className="w-full h-14 rounded-2xl bg-[#EF4444] hover:bg-[#dc2626] text-white text-lg font-bold gap-2">
        <HeartHandshake size={22} /> Quero pedir ajuda a alguém
      </Button>
    </div>
  );
}

function AskForHelp({ ctx, preselect, triggerContext, onNeedNetwork }) {
  const [recipientType, setRecipientType] = useState(preselect);
  const [contactId, setContactId] = useState(null); // null = whole network
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  const network = ctx?.support_network || [];
  const counselor = ctx?.counselor;

  const send = async () => {
    if (!recipientType) { toast.error("Escolha quem você quer chamar."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/help-requests", {
        trigger_context: triggerContext || (recipientType === "counselor" ? "counselor_request" : "support_network_request"),
        recipient_type: recipientType,
        recipient_id: recipientType === "support_network" ? contactId : null,
        message: message || null,
      });
      setDone(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  if (done) {
    return (
      <div className="space-y-5 text-center py-6" data-testid="help-sent-confirmation">
        <div className="w-16 h-16 rounded-full bg-[#34D399]/20 grid place-items-center mx-auto"><CheckCircle2 size={34} className="text-[#0F766E]" /></div>
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1F3B4D]">Pedido de ajuda enviado.</h1>
          <p className="text-sm text-[#94A3B8] mt-1">
            {recipientType === "counselor" ? "Seu conselheiro foi avisado e verá seu pedido." : "Avisamos sua rede de apoio que você gostaria de falar com alguém."}
          </p>
        </div>
        {done.recipients?.length > 0 && (
          <Card className="rounded-2xl p-4 border-[#E0F2FE] text-left inline-block mx-auto">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#0F766E] mb-2">Enviado para</div>
            {done.recipients.map((r, i) => <div key={i} className="text-sm text-[#1F3B4D]">{r.name}{r.phone_display ? ` · ${r.phone_display}` : ""}</div>)}
          </Card>
        )}
        {done.status === "pending_provider" && (
          <p className="text-xs text-[#94A3B8]">O envio por WhatsApp será concluído assim que a integração for ativada.</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#1F3B4D]">Pedir ajuda</h1>
        <p className="text-[15px] text-[#1F3B4D]/70 mt-1">Você não precisa lidar com isso sozinho. Quem você gostaria de chamar agora?</p>
      </div>

      <div className="grid gap-3">
        <button data-testid="ask-card-counselor" onClick={() => setRecipientType("counselor")}
          className={`text-left flex items-center gap-4 p-5 rounded-2xl border-2 transition-all ${recipientType === "counselor" ? "border-[#0F766E] bg-[#0F766E]/5" : "border-[#E0F2FE] bg-white"}`}>
          <div className="w-11 h-11 rounded-xl bg-[#0F766E]/10 grid place-items-center shrink-0"><UserRound size={22} className="text-[#0F766E]" /></div>
          <div><div className="font-semibold text-[#1F3B4D]">{professionalLabel(counselor?.professional_type)}</div><div className="text-sm text-[#94A3B8]">{counselor?.name || "Seu profissional responsável"}</div></div>
        </button>
        <button data-testid="ask-card-network" onClick={() => setRecipientType("support_network")}
          className={`text-left flex items-center gap-4 p-5 rounded-2xl border-2 transition-all ${recipientType === "support_network" ? "border-[#0F766E] bg-[#0F766E]/5" : "border-[#E0F2FE] bg-white"}`}>
          <div className="w-11 h-11 rounded-xl bg-[#34D399]/15 grid place-items-center shrink-0"><Users size={22} className="text-[#0F766E]" /></div>
          <div><div className="font-semibold text-[#1F3B4D]">Minha rede de apoio</div><div className="text-sm text-[#94A3B8]">{network.length} pessoa(s) cadastrada(s)</div></div>
        </button>
      </div>

      {recipientType === "support_network" && (
        network.length === 0 ? (
          <Card className="rounded-2xl p-5 border-[#E0F2FE] text-center">
            <p className="text-sm text-[#1F3B4D]">Você ainda não cadastrou pessoas na sua rede de apoio.</p>
            <Button onClick={onNeedNetwork} data-testid="go-support-network" className="mt-3 rounded-full bg-[#0F766E] hover:bg-[#0d6259]">Cadastrar rede de apoio</Button>
          </Card>
        ) : (
          <Card className="rounded-2xl p-4 border-[#E0F2FE] space-y-2" data-testid="network-picker">
            <button onClick={() => setContactId(null)} className={`w-full text-left px-4 py-3 rounded-xl border ${contactId === null ? "border-[#0F766E] bg-[#0F766E]/5" : "border-[#E0F2FE]"}`} data-testid="pick-all-network">
              <div className="font-semibold text-[#1F3B4D] text-sm">Minha rede de apoio (todos)</div>
              <div className="text-xs text-[#94A3B8]">Avisar todas as pessoas na ordem de prioridade</div>
            </button>
            {network.map((c) => (
              <button key={c.id} onClick={() => setContactId(c.id)} className={`w-full text-left px-4 py-3 rounded-xl border ${contactId === c.id ? "border-[#0F766E] bg-[#0F766E]/5" : "border-[#E0F2FE]"}`} data-testid={`pick-contact-${c.id}`}>
                <div className="font-semibold text-[#1F3B4D] text-sm">{c.name}{c.relation ? ` · ${c.relation}` : ""}</div>
                <div className="text-xs text-[#94A3B8]">{c.phone_display}</div>
              </button>
            ))}
            <p className="text-xs text-[#94A3B8] px-1 pt-1">Vamos avisar sua rede de apoio que você gostaria de falar com alguém.</p>
          </Card>
        )
      )}

      <div>
        <label className="text-sm text-[#1F3B4D] font-medium">Quer acrescentar alguma mensagem? <span className="text-[#94A3B8] font-normal">(opcional)</span></label>
        <Textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="help-message-input"
          placeholder="Escreva se quiser…" className="mt-1 rounded-xl border-[#E0F2FE] min-h-[80px]" />
      </div>

      <Button onClick={send} disabled={sending || (recipientType === "support_network" && network.length === 0)}
        data-testid="send-help-request-button" className="w-full h-14 rounded-2xl bg-[#EF4444] hover:bg-[#dc2626] text-white text-lg font-bold gap-2">
        {sending ? <Loader2 size={22} className="animate-spin" /> : <HeartHandshake size={22} />} Enviar pedido de ajuda
      </Button>
    </div>
  );
}
